import { v5 as uuidv5, v7 as uuidv7 } from 'uuid';
import type { LocalDate } from './time';

/**
 * IDENTIFICADORES DETERMINISTAS — «el truco más rentable del diseño» (PRD 6.5).
 *
 *     entry_id = uuidv5(NAMESPACE_HABIT, `${userId}:${habitId}:${localDate}`)
 *
 * Dos dispositivos sin cobertura que marquen el mismo hábito el mismo día
 * generan **exactamente la misma fila**, no dos. La idempotencia deja de
 * depender de una tabla de mutaciones aplicadas y pasa a ser **una propiedad
 * estructural del esquema**: el `UNIQUE (user_id, habit_id, local_date)` y el id
 * dicen lo mismo, y ninguna carrera puede separarlos.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * EL COROLARIO, que se aplica en todo el producto:
 *
 *     **nunca se sincroniza un interruptor, se sincroniza un estado absoluto.**
 *
 * `toggle(habito, fecha)` no es idempotente ni conmutativo: aplicarlo dos veces
 * deshace lo que hizo, y el orden importa. `setStatus(habito, fecha, 'hecho')`
 * es las dos cosas.
 *
 * La interfaz puede seguir siendo un interruptor —debe serlo—. La mutación que
 * viaja por la red no lo es nunca.
 * ───────────────────────────────────────────────────────────────────────────
 */

/**
 * Espacios de nombres UUID v5. Son constantes del protocolo: **cambiar uno
 * cambia todos los ids que produce y parte el histórico en dos**. Solo se tocan
 * con una migración que reescriba las filas afectadas.
 */
export const NAMESPACE = {
  /** Entradas de hábito: una por hábito y día lógico. */
  habitEntry: '6f1d2a54-8f3e-5b7c-9a10-2c4e6d8f0a12',
  /** Registro de ánimo: uno por día lógico. */
  moodLog: '6f1d2a54-8f3e-5b7c-9a10-2c4e6d8f0a13',
  /** Entrada de diario: una por día lógico. */
  journalEntry: '6f1d2a54-8f3e-5b7c-9a10-2c4e6d8f0a14',
  /** Revisión: una por periodo (`2026-W37`, `2026-09`). */
  reviewSession: '6f1d2a54-8f3e-5b7c-9a10-2c4e6d8f0a15',
  /** Filas que escribe el servidor: ledger, logros, temporadas selladas. */
  servidor: '6f1d2a54-8f3e-5b7c-9a10-2c4e6d8f0a16',
  /** Plan de un día: uno por día lógico. */
  dayPlan: '6f1d2a54-8f3e-5b7c-9a10-2c4e6d8f0a17',
  /** Plan de una semana: uno por semana ISO. */
  weekPlan: '6f1d2a54-8f3e-5b7c-9a10-2c4e6d8f0a18',
  /** Rueda de la vida: una por mes natural (`2026-10`). */
  lifeWheel: '6f1d2a54-8f3e-5b7c-9a10-2c4e6d8f0a19',
} as const;

export type Namespace = (typeof NAMESPACE)[keyof typeof NAMESPACE];

/** El separador nunca aparece dentro de un uuid ni de una fecha: no hay ambigüedad. */
const SEP = ':';

export function habitEntryId(userId: string, habitId: string, localDate: LocalDate): string {
  return uuidv5([userId, habitId, localDate].join(SEP), NAMESPACE.habitEntry);
}

export function moodLogId(userId: string, localDate: LocalDate): string {
  return uuidv5([userId, localDate].join(SEP), NAMESPACE.moodLog);
}

export function journalEntryId(userId: string, localDate: LocalDate): string {
  return uuidv5([userId, localDate].join(SEP), NAMESPACE.journalEntry);
}

/** `periodKey`: `2026-W37` para semanal, `2026-09` para mensual. */
export function reviewSessionId(userId: string, periodType: string, periodKey: string): string {
  return uuidv5([userId, periodType, periodKey].join(SEP), NAMESPACE.reviewSession);
}

/** Un plan por día y usuario: dos dispositivos que planifican el mismo día escriben la misma fila. */
export function dayPlanId(userId: string, localDate: LocalDate): string {
  return uuidv5([userId, localDate].join(SEP), NAMESPACE.dayPlan);
}

export function weekPlanId(userId: string, weekKey: string): string {
  return uuidv5([userId, weekKey].join(SEP), NAMESPACE.weekPlan);
}

/** Una rueda por mes y usuario: dos móviles que valoran el mismo mes escriben la misma fila. */
export function lifeWheelId(userId: string, monthKey: string): string {
  return uuidv5([userId, monthKey].join(SEP), NAMESPACE.lifeWheel);
}

/**
 * Para lo que NO tiene clave natural, porque es un hecho y no un estado: sesiones
 * de foco, filas del ledger, mutaciones.
 *
 * v7 y no v4: lleva el tiempo delante, así que ordena por inserción y los índices
 * de Postgres no se fragmentan al insertar. Un conjunto que solo crece **no
 * puede tener conflictos** (PRD 6.11), y por eso aquí no hace falta determinismo.
 */
export function eventId(): string {
  return uuidv7();
}

/**
 * Id de mutación para la outbox. Lo genera el cliente y viaja con la mutación:
 * es lo que permite al servidor reconocer un reintento y responder «ya estaba»
 * en vez de aplicarla dos veces (entrega at-least-once, PRD 6.4).
 */
export function mutationId(): string {
  return uuidv7();
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID.test(value);
}

/**
 * Ids deterministas de lo que escribe el SERVIDOR.
 *
 * El del ledger se deriva solo del usuario, así que hay **uno por usuario por
 * construcción**, no por una constraint que algún día alguien podría olvidar. El
 * de un logro se deriva de su clave: volver a sellarlo produce el mismo id y el
 * `on conflict do nothing` no hace nada — que es lo que tiene que pasar con un
 * conjunto que solo crece.
 */
export function ledgerId(userId: string): string {
  return uuidv5([userId, 'ledger'].join(SEP), NAMESPACE.servidor);
}

export function achievementId(userId: string, achievementKey: string): string {
  return uuidv5([userId, 'logro', achievementKey].join(SEP), NAMESPACE.servidor);
}

export function sealedSeasonId(userId: string, seasonKey: string): string {
  return uuidv5([userId, 'temporada', seasonKey].join(SEP), NAMESPACE.servidor);
}
